import { useState } from 'react';
import { toast } from 'react-toastify';
import CaseTicketService from './CaseTicketService';

function useCaseTicket() {
  const [caseTickets, setCaseTickets] = useState([]);
  const [caseTicket, setCaseTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCaseTickets = async (params = {}) => {
    setIsLoading(true);
    try {
      const response = await CaseTicketService.getCaseTickets(params);
      setCaseTickets(response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'โหลดรายการเคสไม่สำเร็จ');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCaseTicket = async (id) => {
    setIsLoading(true);
    try {
      const response = await CaseTicketService.getCaseTicket(id);
      setCaseTicket(response.data);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'โหลดเคสไม่สำเร็จ');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const createCaseTicket = async (payload) => {
    try {
      const response = await CaseTicketService.createCaseTicket(payload);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'เปิดเคสไม่สำเร็จ');
      return null;
    }
  };

  const fetchComments = async (id) => {
    try {
      const response = await CaseTicketService.getComments(id);
      setComments(response.data);
    } catch (err) {
      // Silent — the thread just stays empty/stale, not worth a toast on every poll.
    }
  };

  const addComment = async (id, message, action) => {
    try {
      const response = await CaseTicketService.addComment(id, message, action);
      // The socket broadcast may have already reloaded the full thread (it can
      // win the race against this response) — append only if not present, or
      // the poster sees their own bubble twice.
      setComments((prev) => (prev.some((c) => c.id === response.data.id) ? prev : [...prev, response.data]));
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'ส่งข้อความไม่สำเร็จ');
      return null;
    }
  };

  const waitCustomer = async (id) => {
    try {
      await CaseTicketService.waitCustomer(id);
      // Optimistic — the socket broadcast reloads the full ticket right after.
      setCaseTicket((prev) => (prev ? { ...prev, status: 'waiting_customer' } : prev));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
      return false;
    }
  };

  const customerUpdateCaseTicket = async (id, payload) => {
    try {
      const response = await CaseTicketService.customerUpdateCaseTicket(id, payload);
      setCaseTicket(response.data);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
      return null;
    }
  };

  const confirmCustomer = async (id) => {
    try {
      const response = await CaseTicketService.confirmCustomer(id);
      setCaseTicket(response.data);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
      return null;
    }
  };

  const linkRefund = async (id, refundId) => {
    try {
      const response = await CaseTicketService.linkRefund(id, refundId);
      setCaseTicket(response.data);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'เชื่อมรายการโอนคืนไม่สำเร็จ');
      return null;
    }
  };

  const closeCaseTicket = async (id) => {
    try {
      const response = await CaseTicketService.closeCaseTicket(id);
      setCaseTicket(response.data);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'ปิดเคสไม่สำเร็จ');
      return null;
    }
  };

  const reopenCaseTicket = async (id, status) => {
    try {
      const response = await CaseTicketService.reopenCaseTicket(id, status);
      setCaseTicket(response.data);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'เปิดเคสกลับไม่สำเร็จ');
      return null;
    }
  };

  const uploadAttachments = async (id, files) => {
    try {
      const response = await CaseTicketService.uploadAttachments(id, files);
      setCaseTicket((prev) => (prev ? { ...prev, attachments: response.data.attachments, hasNoAttachment: false } : prev));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'อัปโหลดไฟล์ไม่สำเร็จ');
      return false;
    }
  };

  return {
    caseTickets, caseTicket, comments, isLoading,
    fetchCaseTickets, fetchCaseTicket, createCaseTicket, fetchComments, addComment,
    waitCustomer, customerUpdateCaseTicket, confirmCustomer, linkRefund,
    closeCaseTicket, reopenCaseTicket, uploadAttachments,
  };
}

export default useCaseTicket;
